import { type FC } from 'react';
import { Box, Flex, Progress, useBreakpointValue } from '@chakra-ui/react';
import { Helmet } from 'react-helmet';
import { FormattedMessage } from 'react-intl';
import { useTurnToComponentData } from '../../../contexts/useTurnToComponentData';
import { useSLTConfig } from '../../../slt/config/SLTConfigProvider';
import { Button, Header, Rating, Text } from '../../../ui-components';
import { Accordion, AccordionItem } from '../../../ui-components/accordion';
import { GetIcon } from '../../icons';
import type { ProductSummary } from '../../../bff-api/turn-to/model';

export interface ProductReviewProps {
    productSummary: ProductSummary;
    productId: string;
}

export const ProductReview: FC<ProductReviewProps> = ({ productSummary, productId }) => {
    const deviceSize = useBreakpointValue<'mobile' | 'desktop'>({ base: 'mobile', lg: 'desktop' });
    const percentageRating = productSummary.ratingBreakdownPercentage;
    const { onToggleWriteReviewModal } = useTurnToComponentData();
    const { env } = useSLTConfig();

    return (
        <Flex justifyContent="center">
            {productSummary.reviews > 0 && (
                <Box
                    background="white"
                    padding={{ lg: 4 }}
                    display={{ lg: 'flex' }}
                    flexWrap="wrap"
                    gap={8}
                    w="100%"
                >
                    <Box
                        display={{ base: 'none', lg: 'flex' }}
                        justifyContent="space-between"
                        width="100%"
                    >
                        <Header as="h3" variant="header3">
                            <FormattedMessage
                                id="turn-to.rating-number"
                                defaultMessage="{reviewNumber} Ratings"
                                values={{
                                    reviewNumber: productSummary.reviews,
                                }}
                            />
                        </Header>
                        <Button
                            variant="text-button"
                            onClick={() => onToggleWriteReviewModal(true)}
                        >
                            <FormattedMessage
                                id="turn-to.write-a-review"
                                defaultMessage="Write a Review"
                            />
                        </Button>
                    </Box>
                    <Box
                        marginTop={{ base: 2, lg: 0 }}
                        marginBottom={4}
                        textAlign="center"
                        width={{ base: '100%', lg: '28%' }}
                    >
                        <Header
                            as="h2"
                            style="bold"
                            align="center"
                            margin="0 0 0.5rem"
                            variant="header1"
                        >
                            {productSummary.averageRating.toFixed(1)}
                        </Header>
                        <Flex justifyContent="center">
                            <Rating
                                initialRating={productSummary.averageRating}
                                iconSize="lg"
                                disabled
                            />
                        </Flex>
                    </Box>
                    <Box width={{ base: '100%', lg: '30%' }}>
                        {Object.keys(percentageRating)
                            .reverse()
                            .map((key) => (
                                <Box
                                    display="flex"
                                    alignItems="center"
                                    key={key}
                                    marginBottom="12px"
                                    className="space-x-2"
                                >
                                    <Flex className="items-center space-x-2" w="32px">
                                        <GetIcon name="star" size="sm" />
                                        <Text size="small">{key}</Text>
                                    </Flex>
                                    <Progress
                                        flex={1}
                                        w="100%"
                                        maxW="calc(100% - 77px)"
                                        value={
                                            percentageRating[key] === 0
                                                ? 0.1
                                                : percentageRating[key]
                                        }
                                        h="18px"
                                    />
                                    <Flex w="37px" justifyContent="flex-end">
                                        <Text align="right" size="small">
                                            {percentageRating[key] || 0}%
                                        </Text>
                                    </Flex>
                                </Box>
                            ))}
                    </Box>
                    {deviceSize === 'desktop' && (
                        <Flex width="33%" direction="column" alignItems="center">
                            <Text style="bold">
                                <FormattedMessage
                                    defaultMessage="{quantity} Customer Photos"
                                    id="turn-to.quantity-customer-photos"
                                    values={{ quantity: productSummary?.photos?.length }}
                                />
                            </Text>

                            <Box width="100%" marginTop={4}>
                                <div id="tt-gallery-row" />
                            </Box>
                        </Flex>
                    )}
                    <Button
                        variant="primary-black"
                        wide
                        margin="2rem 0"
                        display={{ lg: 'none' }}
                        onClick={() => onToggleWriteReviewModal(true)}
                    >
                        <FormattedMessage
                            defaultMessage="Write a Review"
                            id="turn-to.write-a-review"
                        />
                    </Button>
                    {deviceSize === 'mobile' && !!productSummary?.photos?.length && (
                        <Accordion defaultIndex={0}>
                            <AccordionItem
                                title={
                                    <Text style="bold">
                                        <FormattedMessage
                                            defaultMessage="Customer Photos"
                                            id="turn-to.customer-photos"
                                        />
                                    </Text>
                                }
                            >
                                <Box width="100%" marginTop={2}>
                                    <div id="tt-gallery-row" />
                                </Box>
                            </AccordionItem>
                        </Accordion>
                    )}
                </Box>
            )}
            {productSummary.reviews === 0 && (
                <Flex
                    alignItems="center"
                    flexWrap="wrap"
                    flexDir="column"
                    gap={4}
                    onClick={() => onToggleWriteReviewModal(true)}
                >
                    <Text>
                        <FormattedMessage
                            id="turn-to.be-first"
                            defaultMessage="Be the first to review this item"
                        />
                    </Text>
                    <Rating initialRating={0} iconSize="lg" disabled />
                    <Button
                        variant="primary-black"
                        wide={deviceSize === 'mobile'}
                        margin={deviceSize === 'mobile' ? '0.5rem 0 1rem' : '0.5rem 0 0'}
                    >
                        <FormattedMessage
                            defaultMessage="Write a Review"
                            id="turn-to.write-a-review"
                        />
                    </Button>
                </Flex>
            )}
            {/* Turn to widget to display gallery till they dont have API call for media items */}
            <Helmet>
                <script
                    src={`https://widgets.turnto.com/v5/widgets/${env.turnToSiteKey}/js/turnto.js`}
                    async
                />

                <script>
                    {`var turnToConfig = {
                    locale: "en_US",
                    pageId: "pdp-page",
                    sku: '${productId}',
                    gallery: {
                        skus: ['${productId}'],
'Breakpoint sizes': ['100px', '100px', '100px', '100px', '100px'],
                    },
                };
                window.TurnToCmd=window.TurnToCmd||function(){(TurnToCmd.q=TurnToCmd.q||[]).push(arguments)};`}
                </script>
            </Helmet>
        </Flex>
    );
};
